/**
 * The Intensity system.
 *
 * Authorised by: design-direction §3 ("a global Intensity dial — the single most
 * important control in the app"), its Critic Record objection 1 (per-channel
 * overrides beneath the dial, progressive disclosure) and objection 2 (low-stim is
 * the REFERENCE rendering, the vivid end is the derivation), plus
 * implementation-brief §9 ("Intensity dial as design-token architecture").
 *
 * Architecture:
 *   one dial (0..4) -> six channels -> resolved tokens
 *
 * Every channel may be individually pinned, overriding the dial. `'auto'` follows it.
 * Level 0 is the reference: near-monochrome, still, silent, literal. Every higher
 * level is expressed as a delta from 0 — never the reverse — so the low-stim
 * rendering can never become an afterthought that drifts out of spec. `theme.ts`
 * implements that literally: see `REFERENCE_TOKENS` and `STEP_MULTIPLIERS`.
 *
 * LAW: the dial scales the contrast of DECORATIVE elements only. Text contrast is
 * fixed at accessible levels at every setting (design-direction §3). No channel in
 * this file touches text colour.
 *
 * NO USER-VISIBLE STRINGS LIVE IN THIS FILE. The dial's labels and descriptions are
 * `intensity.level.*`, `intensity.describe.*` and `intensity.channel.*` in
 * `packages/copy/src/catalogue.ts`, in both registers, linted by the copy-law linter
 * (CLAUDE.md: "Every string goes in `packages/copy/src/catalogue.ts` with both
 * registers"). The channel *keys* below are identifiers, not copy — a component maps
 * `channel` to `intensity.channel.${channel}` and asks `useCopy()` for the words.
 */

/**
 * THE VOCABULARY LIVES IN `@carn/core` (RULINGS R29), and this package imports it — the same
 * direction it already imports `Temperature` and `TEMPERATURES`.
 *
 * It used to be declared here, again in `@carn/core`'s `Settings.intensity` as
 * `Record<string, number | 'auto'>`, and again in the schema, so an unknown channel name could
 * persist silently. `@carn/core` is the base layer (`@carn/schema` and `@carn/tokens` both
 * depend on it), and a string union of channel names is pure data with nothing platform-shaped
 * about it. **A new channel is added to `INTENSITY_CHANNELS` in `@carn/core/src/intensity.ts`
 * and nowhere else.**
 *
 * They are re-exported here so every existing `@carn/tokens` consumer keeps working: the
 * vocabulary has one declaration and three entry points, not three declarations.
 */
export {
  DEFAULT_INTENSITY_LEVEL,
  INTENSITY_CHANNELS,
  INTENSITY_LEVELS,
  clampIntensityLevel,
  isIntensityChannel,
  type ChannelSetting,
  type IntensityChannel,
  type IntensityLevel,
  type IntensityOverrides,
  type IntensityPreferences,
} from '@carn/core';

import {
  DEFAULT_INTENSITY_LEVEL,
  INTENSITY_CHANNELS,
  clampIntensityLevel,
  type ChannelSetting,
  type IntensityChannel,
  type IntensityLevel,
  type IntensityPreferences,
} from '@carn/core';

/** The reference rendering. All other levels are deltas from this one. */
export const REFERENCE_LEVEL: IntensityLevel = 0;

export const DEFAULT_INTENSITY: IntensityPreferences = {
  // Steady is the shipped default: the dial's midpoint, not its reference end.
  // Level 0 is the reference for DESIGN (§3 objection 2), not the default for users.
  dial: DEFAULT_INTENSITY_LEVEL,
  overrides: {},
};

/** Resolve one channel's effective level. */
export function channelLevel(
  prefs: IntensityPreferences,
  channel: IntensityChannel,
): IntensityLevel {
  const override = prefs.overrides?.[channel];
  return override === undefined || override === 'auto' ? prefs.dial : override;
}

/** Resolve every channel at once. */
export function resolveChannels(
  prefs: IntensityPreferences,
): Record<IntensityChannel, IntensityLevel> {
  return {
    colour: channelLevel(prefs, 'colour'),
    // RULINGS R10. Note this channel has a FLOOR, not a zero: `theme.ts` solves level 0 to
    // WCAG 1.4.11's 3:1 and derives 1–4 above it. The level resolves the same way as any
    // other; what differs is what the tokens do with it.
    contrast: channelLevel(prefs, 'contrast'),
    decor: channelLevel(prefs, 'decor'),
    motion: channelLevel(prefs, 'motion'),
    haptics: channelLevel(prefs, 'haptics'),
    celebration: channelLevel(prefs, 'celebration'),
    voice: channelLevel(prefs, 'voice'),
  };
}

/** True when any channel has been pinned away from the dial. Drives the settings UI. */
export function hasChannelOverrides(prefs: IntensityPreferences): boolean {
  return INTENSITY_CHANNELS.some((c) => {
    const v = prefs.overrides?.[c];
    return v !== undefined && v !== 'auto';
  });
}

/**
 * Clamp any stored/synced value back into range. Sync from another device or a
 * future app version must never produce an unrenderable intensity.
 */
export function normaliseIntensity(input: unknown): IntensityPreferences {
  const raw = (input ?? {}) as Partial<IntensityPreferences>;
  const dial = clampIntensityLevel(raw.dial, DEFAULT_INTENSITY.dial);
  // A MUTABLE local, because the persisted type is `Readonly` (RULINGS R29): the shape a
  // caller stores must not be amendable in place, and the shape being built here is not yet
  // that. Iterating `INTENSITY_CHANNELS` is also what drops an unknown channel name arriving
  // from sync or a future app version, rather than persisting it unread.
  const overrides: Partial<Record<IntensityChannel, ChannelSetting>> = {};
  for (const channel of INTENSITY_CHANNELS) {
    const v = raw.overrides?.[channel];
    if (v === undefined || v === 'auto') continue;
    overrides[channel] = clampIntensityLevel(v, dial);
  }
  return { dial, overrides };
}

// ---------------------------------------------------------------------------
// OS accessibility
// ---------------------------------------------------------------------------

/**
 * The reading preferences the OS can take out of the user's hands (RULINGS R11).
 *
 * They are NOT Intensity channels — `ReadingPreferences` is its own group in `theme.ts`, and
 * making them channels would put text size under a dial that design-direction §3 forbids from
 * touching text. But they are clamped by the OS in exactly the same way `motion` is, and the
 * settings surface has to be able to say so: "your 'Bolder text' switch does nothing because
 * your phone says bold" is the same sentence as `intensity.systemOverride` and deserves the
 * same mechanism. So `systemClamped` is keyed by a UNION and the notice reads one set.
 */
export const READING_CHANNELS = ['bolderText', 'textScale'] as const;
export type ReadingChannel = (typeof READING_CHANNELS)[number];

/** Anything `Theme.systemClamped` may name. */
export type ClampedChannel = IntensityChannel | ReadingChannel;

/**
 * OS-level accessibility signals collapse channels to the reference regardless of
 * the user's dial. The OS switch wins — a user who set "reduce motion" system-wide
 * should not have to find our slider too.
 *
 * `increaseContrast` is the one that clamps UPWARD (RULINGS R10). Every other switch here
 * takes a channel toward the reference rendering; a user asking their phone for more contrast
 * is asking for the opposite, and the `contrast` channel is the only one where "more" is the
 * accessible direction. Pinning it at maximum is the same act as pinning motion at zero.
 */
export type SystemAccessibility = {
  reduceMotion?: boolean;
  /**
   * iOS "Increase Contrast" / Android high-contrast text. Pins the `contrast` channel at its
   * MAXIMUM regardless of the dial (RULINGS R10, closing DEVIATIONS D23 — one missing channel
   * left this switch unmodelled and a §8.2 clause undeliverable at the same time).
   */
  increaseContrast?: boolean;
  /**
   * iOS "Reduce Transparency". Governs translucency, NOT shadows/grain/contour — it
   * used to be mapped onto the `decor` channel, which was a semantic mismatch: a user
   * asking for opaque surfaces was silently having their texture and depth removed.
   * It now sets `Theme.opaqueSurfaces` and touches no channel.
   */
  reduceTransparency?: boolean;
  /** iOS "Differentiate Without Colour" / Android high-contrast. */
  differentiateWithoutColour?: boolean;
  /** iOS Bold Text. `ReadingPreferences` claims to sit on top of OS scaling; this is it. */
  boldText?: boolean;
  /**
   * THE OS TEXT-SCALE MULTIPLIER ITSELF — `PixelRatio.getFontScale()` — not a boolean
   * (RULINGS R11: the provider stores "the OS text-scale multiplier"; ADA review X1).
   *
   * This used to be `largerText?: boolean`, and the boolean flattened iOS's whole
   * accessibility-size range (~1.15–3.1×) onto one 1.15 floor while RN's own
   * `allowFontScaling` default multiplied the FULL factor on top of the themed size —
   * double-scaled text inside targets that never grew. `resolveTheme` now uses this value
   * as the `reading.textScale` FLOOR, clamped to `READING_BOUNDS.textScale.max`, so the
   * whole theme (type, `minTarget`, spacing, the Interrupt plate) grows together and the
   * 250% layout proofs stay valid on device. `undefined` means "cannot tell", exactly as
   * an absent flag does everywhere else; `1` means the OS is not scaling.
   */
  fontScale?: number;
};

/**
 * The result of folding OS accessibility settings over the user's preferences.
 *
 * `prefs` is a DERIVED value for rendering only. It must never be persisted: the OS
 * clamps live in the same shape as the user's own pins, so writing this back into
 * `Settings.intensity.overrides` (core `settings.ts`) would leave the channel pinned
 * forever after the user turns the OS switch off, and `hasChannelOverrides` would
 * report the mixer as user-customised when the user never touched it.
 *
 * `systemClamped` names the channels the OS took, so a component can render
 * `intensity.systemOverride` ("your device settings are overriding this") instead of
 * showing a control that fights back.
 */
export type SystemAccessibilityResult = {
  prefs: IntensityPreferences;
  systemClamped: ReadonlySet<ClampedChannel>;
};

export function applySystemAccessibility(
  prefs: IntensityPreferences,
  system: SystemAccessibility,
): SystemAccessibilityResult {
  const overrides = { ...prefs.overrides };
  const systemClamped = new Set<ClampedChannel>();

  /**
   * UPWARD (RULINGS R10). Written before the downward clamps so the asymmetry is the first
   * thing a reader of this function meets, rather than a surprise at the end of a list.
   */
  if (system.increaseContrast) {
    overrides.contrast = 4;
    systemClamped.add('contrast');
  }
  /**
   * The reading clamps (RULINGS R11). They touch no override — `resolveTheme` folds them into
   * `ReadingPreferences`, which is where they belong — so this only NAMES them, which is the
   * half that was missing: `boldText` was being applied in two places and reported in none.
   *
   * Named whenever the OS switch is on, not only when it disagrees with the user's own value.
   * With OS bold on, our switch cannot be turned off; a control that silently refuses is the
   * failure R11 exists to close, and it refuses whichever way the user has it set.
   */
  if (system.boldText) systemClamped.add('bolderText');
  // A real multiplier now (RULINGS R11; ADA review X1): named whenever the OS is actually
  // scaling text, i.e. whenever the floor `resolveTheme` applies could hold the control.
  if ((system.fontScale ?? 1) > 1) systemClamped.add('textScale');

  if (system.reduceMotion) {
    overrides.motion = 0;
    systemClamped.add('motion');
    // Particles are motion. `resolveTheme` enforces this for user overrides too
    // (review objection 4.5a) — this cap only makes the OS intent explicit.
    overrides.celebration = Math.min(channelLevel(prefs, 'celebration'), 1) as IntensityLevel;
    systemClamped.add('celebration');
  }
  if (system.differentiateWithoutColour) {
    overrides.colour = 0;
    systemClamped.add('colour');
  }
  // reduceTransparency deliberately clamps NO channel — see the type above.

  return { prefs: { dial: prefs.dial, overrides }, systemClamped };
}
